import { gql, useMutation, useQuery } from '@apollo/client'
import uniqBy from 'lodash/uniqBy'
import { useMemo, useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistDeleteEvent,
  ArtistDeleteEventMutation,
  ArtistDeleteEventMutationVariables,
  ArtistUpdateEvent,
  ArtistUpdateEventMutation,
  ArtistUpdateEventMutationVariables,
  ConsoleEventListFragmentDoc,
  ConsoleEventsQuery,
  ConsoleEventsQueryVariables,
  EventFilter,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const params = new URLSearchParams(window.location.search)
  const eventFilter = params.get('sort')
  // sort と EventFilter を比較して一致する値を取得
  const initialSelectedValue = Object.values(EventFilter).includes(
    eventFilter as EventFilter
  )
    ? eventFilter
    : EventFilter.All

  const [filter, setFilter] = useState<EventFilter>(
    initialSelectedValue as EventFilter
  )

  const { isFirstLoadRef } = useGetNewestData<
    ConsoleEventsQuery,
    ConsoleEventsQueryVariables
  >({
    query: QUERY,
    variables: { filter: filter },
    fields: [
      {
        fragmentName: 'ConsoleEventList',
        fieldName: 'artistEvents',
        fragment: ConsoleEventListFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore, refetch } = useQuery<
    ConsoleEventsQuery,
    ConsoleEventsQueryVariables
  >(QUERY, {
    variables: { filter: filter },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const [artistUpdateEvent, { loading: changingEventStatus }] = useMutation<
    ArtistUpdateEventMutation,
    ArtistUpdateEventMutationVariables
  >(ArtistUpdateEvent)

  const [artistDeleteEvent, { loading: deletingEvent }] = useMutation<
    ArtistDeleteEventMutation,
    ArtistDeleteEventMutationVariables
  >(ArtistDeleteEvent)

  // fixed issue duplicate events on graphql cached
  const events = useMemo(() => {
    if (!data?.artistEvents) {
      return null
    }
    return {
      ...data.artistEvents,
      edges: uniqBy(data.artistEvents.edges, 'node.id'),
    }
  }, [data?.artistEvents])

  return {
    state: {
      events,
      loading,
      currentUser: user,
      artist: data?.artist ?? null,
      changingEventStatus,
      deletingEvent,
      hasNextPage: !!data?.artistEvents?.pageInfo.hasNextPage,
      artistId: user?.uid ?? '',
      filter: filter,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            filter,
            pageInput: {
              cursor: data?.artistEvents?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFilter(filterType: EventFilter) {
        if (filterType === filter) return

        refetch({
          filter: filterType,
          pageInput: {
            cursor: null,
          },
        })

        setFilter(filterType)
      },
      async onChangeEventStatus(eventId, status) {
        await artistUpdateEvent({
          variables: { id: eventId, input: { status } },
        })
      },
      async onDeleteEvent(eventId) {
        await artistDeleteEvent({ variables: { id: eventId } })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleEvents($filter: EventFilter!, $pageInput: PageInput) {
    artistEvents(filter: $filter, pageInput: $pageInput) {
      edges {
        node {
          ... on Event {
            ...ConsoleEventList
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${ConsoleEventListFragmentDoc}
`
